import React from "react";
import api from "../../lib/api";
import { Plus, X, Trash2, Star, StarOff } from "lucide-react";

const CATS = ["celebrations", "maternite", "portrait", "branding", "studio", "atelier-lumiere"];

export default function AdminPortfolio() {
  const [images, setImages] = React.useState([]);
  const [showAdd, setShowAdd] = React.useState(false);

  const load = () => api.get("/portfolio").then(r => setImages(r.data));
  React.useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette image ?")) return;
    await api.delete(`/admin/portfolio/${id}`);
    load();
  };
  const toggleFeatured = async (img) => {
    await api.patch(`/admin/portfolio/${img.id}`, { featured: !img.featured });
    load();
  };

  return (
    <div data-testid="admin-portfolio">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Galerie</div>
          <h1 className="h-display text-4xl md:text-5xl">Portfolio.</h1>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="add-image-btn"><Plus size={14}/> Ajouter une image</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {images.map(img => (
          <div key={img.id} className="relative aspect-[3/4] group" data-testid={`portfolio-admin-${img.id}`}>
            <img src={img.url} alt="" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-dark/60 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center gap-2">
              <div className="eyebrow text-accent-light">{img.category}</div>
              <div className="flex gap-2">
                <button onClick={() => toggleFeatured(img)} className="p-2 border border-white/30 hover:border-accent-deep" title="Featured">
                  {img.featured ? <Star size={14} className="fill-accent-deep stroke-accent-deep"/> : <StarOff size={14}/>}
                </button>
                <button onClick={() => remove(img.id)} className="p-2 border border-white/30 hover:border-red-400" title="Supprimer">
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
            {img.featured && <span className="absolute top-2 left-2 bg-accent-deep/90 text-dark text-[0.6rem] uppercase tracking-widest px-2 py-0.5">Featured</span>}
          </div>
        ))}
      </div>
      {showAdd && <AddImageModal onClose={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}

function AddImageModal({ onClose }) {
  const [form, setForm] = React.useState({ url: "", category: "portrait", titre: "", featured: false, ordre: 0 });
  const [loading, setLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState("");

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      // Do NOT set Content-Type manually — axios/browser will add the correct multipart boundary.
      const r = await api.post("/admin/upload", fd, { timeout: 300000 });
      // Build absolute URL so the <img> preview and saved record work cross-origin
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const absUrl = r.data.url.startsWith("http") ? r.data.url : `${base}${r.data.url}`;
      setForm(s => ({ ...s, url: absUrl }));
    } catch (err) {
      const status = err?.response?.status;
      let msg = err?.response?.data?.detail || "Erreur d'upload.";
      if (status === 401) {
        msg = "Ta session a expiré. Reconnecte-toi, puis retente.";
      } else if (status === 413) {
        msg = "Fichier trop lourd (max 100 MB). Compresse-le ou exporte en JPG.";
      }
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.url) return;
    setLoading(true);
    try {
      await api.post("/admin/portfolio", form);
      onClose();
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="bg-dark border border-white/15 p-8 w-full max-w-lg" data-testid="add-image-modal">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-serif text-2xl">Nouvelle image</h3>
          <button type="button" onClick={onClose}><X size={20}/></button>
        </div>
        <div className="space-y-6">
          <div>
            <label className="eyebrow mb-2 block">Fichier (JPG / PNG / WEBP / HEIC)</label>
            <input type="file" accept="image/*,.heic,.heif" onChange={handleFile} disabled={uploading} className="input-editorial disabled:opacity-50" data-testid="upload-file"/>
            {uploading && <div className="text-xs text-accent-light mt-2">Conversion et optimisation en cours…</div>}
            {uploadError && <div className="text-xs text-red-400 mt-2" data-testid="upload-error">{uploadError}</div>}
          </div>
          <div className="text-center eyebrow text-text-cream/30">— ou —</div>
          <div>
            <label className="eyebrow mb-2 block">URL externe</label>
            <input type="url" value={form.url.startsWith("data:") ? "" : form.url} onChange={(e)=>setForm({...form, url:e.target.value})} className="input-editorial" placeholder="https://..." data-testid="upload-url"/>
          </div>
          {form.url && <img src={form.url} alt="" className="w-full max-h-48 object-cover" data-testid="upload-preview"/>}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="eyebrow mb-2 block">Catégorie</label>
              <select value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} className="input-editorial bg-dark" data-testid="upload-category">
                {CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="eyebrow mb-2 block">Ordre</label>
              <input type="number" value={form.ordre} onChange={(e)=>setForm({...form, ordre:Number(e.target.value)})} className="input-editorial"/>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.featured} onChange={(e)=>setForm({...form, featured:e.target.checked})} className="accent-[var(--accent-deep)]"/>
            <span className="font-light">Mettre en featured (page d'accueil)</span>
          </label>
        </div>
        <button type="submit" disabled={loading || uploading || !form.url} className="btn-primary mt-8 w-full justify-center disabled:opacity-40" data-testid="upload-submit">
          {loading ? "Ajout…" : "Ajouter l'image"}
        </button>
      </form>
    </div>
  );
}
